import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useCurrentRole} from "@hooks/CurrentRole";
import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {UploadFileProgressPopup} from "@popups/uploadPopups/UploadFileProgressPopup";
import {
  decrementTotalData,
  getUsers,
  setInactiveData,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalData,
  setTotalPages,
} from "@redux/slices/userSlices/tableSlice";
import {
  archiveUser,
  deleteUser,
  unArchiveUser,
} from "@redux/slices/userSlices/viewSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const UserTablePage = () => {
  const {openPopup, closePopup} = usePopup();
  const currentRole = useCurrentRole();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo, id, role} = useSelector((state) => state.common.candidate);
  const {
    inactiveData,
    data,
    page,
    sort,
    size,
    search,
    totalPages,
    totalData,
    selectedData,
    status,
  } = useSelector((state) => state.user.table);

  const [params, setParams] = useState({
    page: 1,
    size: 5,
    with_archived: inactiveData,
    sort,
  });

  const handleOpenPopup = () => {
    openPopup(<UploadFileProgressPopup type={"csv"} />);
  };

  const handleClickArchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map(
          (user) =>
            !user.archived_at &&
            user.id !== id &&
            user.role !== "Admin" &&
            dispatch(archiveUser({id: user.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        archiveUser.fulfilled.match(result),
      );

      if (
        successfulResults.every((result) => archiveUser.fulfilled.match(result))
      ) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        if (!inactiveData) {
          dispatch(setTotalData(totalData - successfulResults.length));
        }

        setParams(newParams);
        dispatch(getUsers(newParams));
      }
    } catch (e) {
      console.log("Error archiving all users: ", e);
    }
  }, [page, size, search, sort, inactiveData, totalData, selectedData]);

  const handleClickUnarchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map(
          (user) =>
            user.archived_at &&
            user.id !== id &&
            user.role !== "Admin" &&
            dispatch(unArchiveUser({id: user.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        archiveUser.fulfilled.match(result),
      );

      if (
        successfulResults.every((result) =>
          unArchiveUser.fulfilled.match(result),
        )
      ) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        setParams(newParams);
        dispatch(getUsers(newParams));
      }
    } catch (e) {
      console.log("Error unarchiving all users: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedData]);

  const removeAllUsers = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map((user) => dispatch(deleteUser({id: user?.id}))),
      );

      if (results.every((result) => deleteUser.fulfilled.match(result))) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(setTotalData(totalData - results.length));
        setParams(newParams);
        dispatch(getUsers(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error removing all users: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedData, totalData]);

  const handleCheckFilter = () => {
    dispatch(setPage(1));
    dispatch(setInactiveData(!inactiveData));
  };

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    dispatch(setTotalPages(Math.ceil(totalPages / size)));
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value.toLowerCase()));
        dispatch(getUsers({page: 1, size, search: value}));
      }, 500);
    },
    [dispatch, size],
  );

  const handleClickDeleteAll = () => {
    openPopup(
      <DeleteDataPopup
        onClickNo={() => closePopup()}
        onClickYes={() => removeAllUsers()}
      />,
      {closeButton: false},
    );
  };

  const handleClickArchiveUser = async (item) => {
    try {
      const resultAction = await dispatch(archiveUser({id: item.id}));

      if (archiveUser.fulfilled.match(resultAction)) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        if (!inactiveData) {
          dispatch(decrementTotalData());
        }

        setParams(newParams);
        dispatch(getUsers(newParams));
      }
    } catch (e) {
      console.log("Error archiving user: ", e);
    }
  };

  const handleClickUnarchiveUser = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(unArchiveUser({id: item.id}));

        if (unArchiveUser.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          setParams(newParams);
          dispatch(getUsers(newParams));
        }
      } catch (e) {
        console.log("Error unarchiving user: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const removeUser = useCallback(
    async (id) => {
      try {
        const resultAction = await dispatch(deleteUser({id}));

        if (deleteUser.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          dispatch(decrementTotalData());
          setParams(newParams);
          dispatch(getUsers(newParams));
          closePopup();
        }
      } catch (e) {
        console.log("Error deleting user: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const handleClickDeleteUser = async (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => removeUser(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--orange)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    ...(role === "Admin"
      ? [
          {
            id: 1,
            button: (
              <ControlButton
                color={"var(--violetLight)"}
                txt={t("button.upload_data")}
                onClick={() => handleOpenPopup()}
              />
            ),
          },
        ]
      : []),
    {
      id: 2,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("button.deactivate")}
          onClick={() => handleClickArchiveAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((user) => !user.archived_at)
          }
        />
      ),
    },
  ];

  const advancedControls = [
    {
      id: 3,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("table.button.activate")}
          onClick={() => handleClickUnarchiveAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((user) => user.archived_at) ||
            selectedData.some((user) => !user.archived_at)
          }
        />
      ),
    },
    {
      id: 4,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("table.button.delete")}
          onClick={() => handleClickDeleteAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((user) => user.archived_at) ||
            selectedData.some((user) => !user.archived_at)
          }
        />
      ),
    },
  ];

  const columns = useMemo(() => {
    const baseColumns = [
      {Header: t("table.user"), accessor: "name", width: 200},
      {Header: "Email", accessor: "email", width: 220},
      {
        Header: t("table.created"),
        accessor: "created_at",
        Cell: ({cell: {value}}) =>
          value ? moment(value).format("DD-MM-YYYY") : "",
        width: 215,
      },
      {
        Header: t("table.role"),
        accessor: "role",
        Cell: ({cell: {value}}) => {
          return <>{currentRole(value)}</>;
        },
        width: 305,
      },
      {
        Header: t("table.product_line"),
        accessor: "product_line",
        width: 265,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            className={"flex gap-[8rem] phone:gap-[40rem]"}
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <>
                {!row.original.archived_at && (
                  <TableButton
                    icon={"edit"}
                    color={"var(--blueLight)"}
                    label={t("table.button.edit")}
                    action={"edit"}
                    item={row.original}
                  />
                )}

                {row.original.archived_at ? (
                  <>
                    <TableButton
                      icon={"activate"}
                      color={"var(--green)"}
                      label={t("table.button.activate")}
                      action={"activate"}
                      onClickAction={() =>
                        handleClickUnarchiveUser(row.original)
                      }
                      item={row.original}
                    />

                    <TableButton
                      icon={"delete"}
                      color={"var(--red)"}
                      label={t("table.button.delete")}
                      action={"delete"}
                      onClickAction={() => handleClickDeleteUser(row.original)}
                      item={row.original}
                    />
                  </>
                ) : (
                  <TableButton
                    icon={"deactivate"}
                    color={"var(--red)"}
                    label={t("table.button.deactivate")}
                    action={"deactivate"}
                    onClickAction={() => handleClickArchiveUser(row.original)}
                    item={row.original}
                    disabled={
                      row.original.role === "Admin" ||
                      row.original.id === id ||
                      row.original.archived_at
                    }
                  />
                )}
              </>
            )}
          </div>
        ),
        width: 210,
      },
    ];

    if (!demo) {
      const allAdmins = data.every(
        (user) => user.role === "Admin" || user.id === id,
      );

      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <RootCheckbox
            disabled={allAdmins}
            {...getToggleAllRowsSelectedProps()}
          />
        ),
        Cell: ({row}) => (
          <RootCheckbox
            disabled={row.original.role === "Admin" || row.original.id === id}
            {...row.getToggleRowSelectedProps()}
          />
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, page, size, search, sort, inactiveData, data]);

  useEffect(() => {
    const newParams = {
      with_archived: inactiveData,
      ...(page > 0 ? {page} : {page: 1}),
      ...(size > 0 ? {size} : {size: 5}),
      ...(search && {search}),
      ...(Object.keys(sort).length !== 0 && {sort}),
    };

    setParams(newParams);
  }, [page, size, search, sort, inactiveData]);

  useDeepCompareEffect(() => {
    dispatch(getUsers(params));
  }, [params]);

  // calculate total pages when totalData changes
  useEffect(() => {
    dispatch(setTotalPages(Math.ceil(totalData / size)));
  }, [totalData]);

  // calculate page when page > totalPages and data is empty
  useEffect(() => {
    if (page === totalPages + 1) {
      dispatch(setPage(Math.ceil(totalData / size)));
    }
  }, [totalPages]);

  return (
    <ScreenLayout
      title={t("page.users.title")}
      controlsList={
        !demo && [...controls, ...(inactiveData ? advancedControls : [])]
      }
    >
      <TableLayout
        serverStatus={status}
        columns={columns}
        data={data}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_users")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
        filterCheckbox={
          !demo && (
            <RootCheckbox
              checked={inactiveData}
              onChange={() => handleCheckFilter()}
              label={t("page.users.checkbox_filter")}
            />
          )
        }
      />
    </ScreenLayout>
  );
};
